/**
 * Outil « Pythagore en carrés » (Boîte à outils, 4e → 2nde).
 *
 * Un triangle à sommets sur le quadrillage, un carré posé sur chaque côté
 * avec son aire. On déplace les sommets au doigt : quand l'angle opposé au
 * plus grand côté vaut 90°, la somme des deux petits carrés égale le grand
 * (théorème et réciproque) ; sinon l'égalité casse (contraposée). Défis :
 * triangles rectangles « penchés ». Calculs exacts dans `pythagoras.ts`.
 */

import { useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { DragHandle, INK, PlotGrid, RightAngleMark, FIGURE_BG_CLASS } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  analyse,
  GOALS,
  lengthText,
  outerSquare,
  SIDES,
  sideName,
  squaredLength,
  type Side,
  type Triangle,
  type Vertex,
} from './pythagoras';

const SIZE = 320;
const VP = { xMin: -8, xMax: 8, yMin: -8, yMax: 8 };
const frame = makeFrame(VP, SIZE);
const VERTICES: readonly Vertex[] = ['A', 'B', 'C'];
const START: Triangle = { A: { x: -1, y: 1 }, B: { x: 3, y: -2 }, C: { x: -1, y: -2 } };

const LEG_COLORS = ['var(--color-primary-500)', 'var(--color-accent-500)'] as const;
const HYP_COLOR = 'var(--color-xp-500)';

/** Les trois carrés tiennent-ils dans la figure ? (sinon on refuse le geste) */
function fits(t: Triangle): boolean {
  return SIDES.every((s) =>
    outerSquare(t, s).every(
      (p) => p.x >= VP.xMin && p.x <= VP.xMax && p.y >= VP.yMin && p.y <= VP.yMax,
    ),
  );
}

function distinct(t: Triangle): boolean {
  return SIDES.every((s) => squaredLength(t, s) > 0);
}

export function PythagorasTool({ onSend, sendCost }: ToolProps) {
  const [tri, setTri] = useState<Triangle>(START);
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drag = useDrag<Vertex>({
    svgRef,
    frame,
    ids: VERTICES,
    pointOf: (v) => tri[v],
    onDrag: (v, p) =>
      setTri((old) => {
        const next = { ...old, [v]: snapPoint(p, VP, 1, 1) };
        return distinct(next) && fits(next) ? next : old;
      }),
  });

  const a = analyse(tri);
  const legs = [...a.legs].sort((x, y) => sideName(x).localeCompare(sideName(y))) as [Side, Side];
  const legSq = legs.map((s) => squaredLength(tri, s)) as [number, number];
  const sum = legSq[0] + legSq[1];
  const hypName = sideName(a.longest);
  const colorOf = (s: Side) => (s === a.longest ? HYP_COLOR : LEG_COLORS[legs.indexOf(s) as 0 | 1]);
  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const goalDone = goal !== null && GOALS[goal]!.check(tri);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleChip on={goal !== null} onClick={() => setGoal((g) => (g === null ? 0 : null))}>
          Défi
        </ToggleChip>
        <button
          type="button"
          onClick={() => setTri(START)}
          className="text-ink-600 hover:bg-ink-50 min-h-11 rounded-xl px-3 text-sm font-semibold active:scale-[0.98]"
        >
          Triangle 3-4-5
        </button>
      </div>

      {goal !== null && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="pythagoras-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {goalDone ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  onClick={() => setGoal(goal + 1)}
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">Déplace les sommets A, B ou C.</p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[24rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label="Triangle ABC et les carrés posés sur ses côtés"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} axes={false} />
          {!a.degenerate &&
            SIDES.map((s) => {
              const sq = outerSquare(tri, s).map(px);
              const area = squaredLength(tri, s);
              const cx = (sq[0]!.x + sq[2]!.x) / 2;
              const cy = (sq[0]!.y + sq[2]!.y) / 2;
              return (
                <g key={s} data-testid={`square-${sideName(s)}`}>
                  <polygon
                    points={sq.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill={colorOf(s)}
                    fillOpacity={0.22}
                    stroke={colorOf(s)}
                    strokeWidth={1.5}
                  />
                  {area >= 2 && (
                    <text
                      x={cx}
                      y={cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={area >= 5 ? 15 : 12}
                      fontWeight={700}
                      fill={INK}
                    >
                      {area}
                    </text>
                  )}
                </g>
              );
            })}
          <polygon
            points={VERTICES.map((v) => `${frame.tx(tri[v].x)},${frame.ty(tri[v].y)}`).join(' ')}
            fill="var(--color-ink-200)"
            fillOpacity={0.35}
            stroke={INK}
            strokeWidth={2}
            strokeLinejoin="round"
          />
          {a.right &&
            (() => {
              const v = px(tri[a.vertex]);
              const [p, q] = [a.longest[0] as Vertex, a.longest[1] as Vertex].map((w) =>
                px(tri[w]),
              );
              return (
                <RightAngleMark
                  x={v.x}
                  y={v.y}
                  ux={p!.x - v.x}
                  uy={p!.y - v.y}
                  vx={q!.x - v.x}
                  vy={q!.y - v.y}
                />
              );
            })()}
          {VERTICES.map((v) => {
            const p = px(tri[v]);
            // le nom se place à l'opposé du centre du triangle
            const gx = (frame.tx(tri.A.x) + frame.tx(tri.B.x) + frame.tx(tri.C.x)) / 3;
            const gy = (frame.ty(tri.A.y) + frame.ty(tri.B.y) + frame.ty(tri.C.y)) / 3;
            const d = Math.hypot(p.x - gx, p.y - gy) || 1;
            return (
              <DragHandle
                key={v}
                x={p.x}
                y={p.y}
                color={INK}
                label={v}
                labelDx={((p.x - gx) / d) * 20}
                labelDy={((p.y - gy) / d) * 20}
                name={`Sommet ${v} (${tri[v].x} ; ${tri[v].y})`}
                active={drag.active === v}
                keyProps={drag.keyProps(v)}
              />
            );
          })}
        </svg>
      </div>

      {a.degenerate ? (
        <Note>Les trois points sont alignés : ce n’est plus un triangle.</Note>
      ) : (
        <Note tone={a.right ? 'success' : 'error'}>
          <span
            className="font-display flex flex-wrap gap-x-2 text-base font-bold"
            data-testid="pythagoras-equation"
          >
            <span className="whitespace-nowrap">
              {hypName}² = {a.longestSq}
            </span>{' '}
            <span className="whitespace-nowrap">{a.right ? 'et' : 'mais'}</span>{' '}
            <span className="whitespace-nowrap">
              {sideName(legs[0])}² + {sideName(legs[1])}² = {legSq[0]} + {legSq[1]} = {sum}
            </span>
          </span>
          <span className="mt-1 block">
            {a.right
              ? `Le triangle est rectangle en ${a.vertex} : l’égalité de Pythagore est vraie (théorème), et inversement elle prouve l’angle droit (réciproque).`
              : `Angle en ${a.vertex} ≈ ${formatNumber(a.angleDeg, 1)}° : l’égalité est fausse, donc le triangle n’est pas rectangle (contraposée).`}
          </span>
          <span className="text-ink-600 mt-1 block">
            On teste toujours le plus grand côté, ici [{hypName}].
          </span>
        </Note>
      )}

      <section aria-label="Longueurs des côtés" className="space-y-2">
        {SIDES.map((s) => {
          const sq = squaredLength(tri, s);
          const len = lengthText(sq);
          return (
            <div
              key={s}
              className="border-ink-100 flex min-h-12 items-center gap-3 rounded-2xl border px-3 py-1.5"
            >
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ background: colorOf(s) }}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block text-base font-semibold">
                  {sideName(s)} = {len.plain}
                  {!len.exact && (
                    <span className="text-ink-500 text-sm font-medium">
                      {' '}
                      ≈ {formatNumber(Math.sqrt(sq), 2)}
                    </span>
                  )}
                </span>
                <span className="text-ink-500 block text-sm">
                  {sideName(s)}² = {sq}
                </span>
              </span>
              {onSend && (
                <SendToSheetButton
                  label={len.plain}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: len.atoms })}
                />
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
